import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import Dashboard from "./Dashboard";

/**
 * ClientInvoicesPage — wrapper that resolves a customer's slug to a company_id
 * and renders the existing Dashboard component without the admin sidebar.
 *
 *   URL: /{slug}/invoices
 *
 * Pushes `?company=<id>` into the URL so the existing Dashboard logic works
 * unchanged.  Adds a slim white-label header at the top.
 */
export default function ClientInvoicesPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [company, setCompany] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/companies/by-slug/${slug}`);
        if (!alive) return;
        setCompany(data);
        // Inject company id into URL (preserve other params like ?doc=)
        const sp = new URLSearchParams(window.location.search);
        if (!sp.get("company")) {
          sp.set("company", data.id);
          const newUrl = `${window.location.pathname}?${sp.toString()}`;
          window.history.replaceState({}, "", newUrl);
        }
      } catch (e) {
        if (alive) setError(
          e.response?.status === 403
            ? "אין לך הרשאה"
            : (e.response?.data?.detail || "החברה לא נמצאה")
        );
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  // Owner trying to access another company? push them out.
  useEffect(() => {
    if (!user || !company) return;
    if (user.role === "owner" && user.company_id !== company.id) {
      navigate("/login", { replace: true });
    }
  }, [user, company, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" dir="rtl">
        <p className="text-rose-600">{error}</p>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="min-h-screen flex items-center justify-center" dir="rtl">
        <p className="text-stone-500">טוען...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" dir="rtl">
      {/* Slim white-label header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate(`/${slug}`)}
            className="flex items-center gap-3 min-w-0 group"
            data-testid="client-back-home"
          >
            {company.logo_url ? (
              <div className="h-10 w-10 rounded-xl bg-white border border-stone-200 overflow-hidden flex items-center justify-center shadow-sm shrink-0 group-hover:scale-105 transition">
                <img src={company.logo_url} alt={company.name} className="h-full w-full object-contain p-0.5" />
              </div>
            ) : (
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-fuchsia-500 to-emerald-500 flex items-center justify-center text-white font-bold text-lg shrink-0">
                {(company.name || "?").trim().charAt(0).toUpperCase()}
              </div>
            )}
            <div className="text-right">
              <h1 className="font-heading font-bold text-base sm:text-lg truncate">{company.display_name || company.name}</h1>
              <p className="text-[10px] text-stone-500 truncate">לחצי לחזרה לדשבורד</p>
            </div>
          </button>
        </div>
      </header>

      {/* Wrap the existing Dashboard - it will pick up ?company=... from URL */}
      <Dashboard />
    </div>
  );
}
